import type React from "react";
import {memo, useCallback} from "react";
import {Linking} from "react-native";
import {Link as LinkRouter} from "react-router-dom";

import * as LinkInternal from "../ui/link";
import {Pressable} from "../ui/pressable";

type PropsExternal = {
    type: "external";
    href: string;
    target?: "_blank" | "_self";
};

type Props = {
    testID?: string;
    variant?: "xs" | "sm" | "md";
    color?:
        | "blueGray.400"
        | "blueGray.600"
        | "blue.600"
        | "success.600"
        | "warning.600"
        | "red.600";
    className?: string;
    children: React.ReactNode;
} & StrictUnion<
    | {
          to: string;
      }
    | {
          onPressOnApp: () => void;
      }
    | PropsExternal
>;

/** How to use it
 * internal on web <Link to="/">Internal link</Link>
 * external on web <Link type="external" href="https://google.com">External link</Link>
 * on internal app <Link onPressOnApp={() => console.log("open then link with navigation")}>On app</Link>
 * on external app <Link type="external" href="https://google.com">External link</Link>
 */
const Link: React.FC<Props> = ({
    testID,
    children,
    color = "blue.600",
    target = "_blank",
    className,
    type,
    href,
    variant = "sm",
    to,
    onPressOnApp
}) => {
    // always open on the browser on app
    const handleOpenLink = useCallback(() => {
        if (type === "external") {
            Linking.openURL(href);
        }
    }, [type, href]);

    const elem =
        typeof children === "string" ? (
            <LinkInternal.LinkText color={color} variant={variant}>
                {children}
            </LinkInternal.LinkText>
        ) : (
            children
        );

    if (process.env.IS_WEB) {
        if (type === "external") {
            return (
                <a
                    className={className}
                    data-testid={testID}
                    href={href}
                    rel="noopener noreferrer"
                    target={target}
                >
                    {elem}
                </a>
            );
        }
        if (to) {
            return (
                <LinkRouter className={className} data-testid={testID} to={to}>
                    {elem}
                </LinkRouter>
            );
        }
    }

    if (type === "external") {
        return (
            <Pressable className={className} testID={testID} onPress={handleOpenLink}>
                {elem}
            </Pressable>
        );
    }
    return (
        <Pressable className={className} testID={testID} onPress={onPressOnApp}>
            {elem}
        </Pressable>
    );
};

export default memo(Link);
